import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';

const IMG = `${process.env.PUBLIC_URL}/images`;
const CIMG = `${IMG}/careers`;

const heroChips = [
  { icon: 'lightbulb', title: 'Innovation', sub: 'First Mindset' },
  { icon: 'users', title: 'Collaborative', sub: 'Culture' },
  { icon: 'heart', title: 'Meaningful', sub: 'Impact' },
  { icon: 'trending-up', title: 'Continuous', sub: 'Growth' },
];

const benefits = [
  {
    icon: 'lightbulb',
    title: 'Meaningful Work',
    desc: 'Solve real problems and build products that impact millions of celebrations.',
  },
  {
    icon: 'users',
    title: 'Great People',
    desc: 'Work with passionate, talented and supportive colleagues.',
  },
  {
    icon: 'rocket',
    title: 'Growth & Learning',
    desc: 'Continuous learning, mentorship and career growth opportunities.',
  },
  {
    icon: 'heart',
    title: 'Work with Purpose',
    desc: 'Be part of a mission that spreads joy and creates lasting memories.',
  },
  {
    icon: 'globe',
    title: 'Inclusive Culture',
    desc: 'A diverse, inclusive and respectful environment for everyone.',
  },
  {
    icon: 'trophy',
    title: 'Recognition',
    desc: 'Celebrate wins and recognize contributions that drive our success.',
  },
];

const life = [
  'Flexible & Hybrid Work Environment',
  'Learning & Development Programs',
  'Innovation Labs & Hackathons',
  'Wellness & Well-being Initiatives',
  'Fun, Celebrations & Team Bonding',
];

const stats = [
  { icon: 'users', value: '500+', label: 'Team Members' },
  { icon: 'lightbulb', value: '25+', label: 'Cities Represented' },
  { icon: 'heart', value: '40%', label: 'Women in Workforce' },
  { icon: 'smile', value: '4.8/5', label: 'Employee Happiness' },
  { icon: 'book', value: '100+', label: 'Learning Programs' },
  { icon: 'infinity', value: '∞', label: 'Opportunities' },
];

export default function Careers() {
  return (
    <div className="careers-page">
      {/* =================== HERO =================== */}
      <section className="cp-hero">
        <div
          className="cp-hero__bg"
          style={{ backgroundImage: `url(${CIMG}/careers-2.jpeg)` }}
        />
        <div className="container container--wide cp-hero__inner">
          <div className="crumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Careers</span>
          </div>

          <div className="cp-hero__copy">
            <h1 className="cp-hero__title">
              Build the Future.
              <br />
              Create Impact.
              <br />
              <span className="grad-pink">Spread Joy.</span>
            </h1>
            <p className="cp-hero__lead">
              At Caladium Systems, we are building intelligent ecosystems that
              transform celebrations and experiences for millions. Join a team
              of dreamers, builders and doers who are passionate about solving
              real problems with technology and creating meaningful impact.
            </p>
            <div className="cp-hero__actions">
              <Link to="#roles" className="btn btn--primary">
                Explore Open Roles <Icon name="arrow-right" size={18} />
              </Link>
              <button type="button" className="btn btn--ghost">
                <Icon name="play" size={14} /> Life at Caladium
              </button>
            </div>

            <div className="cp-chips">
              {heroChips.map((c) => (
                <div className="cp-chip" key={c.title}>
                  <span className="cp-chip__icon">
                    <Icon name={c.icon} size={18} />
                  </span>
                  <div className="cp-chip__text">
                    <strong>{c.title}</strong>
                    <span>{c.sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== WHY LOVE WORKING =================== */}
      <section className="cp-benefits-sec">
        <div className="container container--wide">
          <div className="cp-benefits">
            <h2 className="cp-benefits__title">Why You&rsquo;ll Love Working Here</h2>
            <div className="cp-benefits__grid">
              {benefits.map((b) => (
                <div className="cp-benefit" key={b.title}>
                  <span className="cp-benefit__icon">
                    <Icon name={b.icon} size={28} />
                  </span>
                  <h3>{b.title}</h3>
                  <p>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== LIFE AT CALADIUM =================== */}
      <section className="cp-life-sec">
        <div className="container container--wide cp-life">
          <div className="cp-life__copy">
            <span className="eyebrow">LIFE AT CALADIUM</span>
            <h2 className="h2">
              Where Ideas <span className="grad-pink">Thrive</span>
              <br />
              and People <span className="grad-pink">Grow</span>
            </h2>
            <p>
              We believe the best ideas come from empowered people in a culture
              of trust, autonomy and collaboration.
            </p>
            <ul className="cp-list">
              {life.map((l) => (
                <li key={l}>
                  <span className="cp-list__dot">
                    <Icon name="check" size={12} />
                  </span>
                  {l}
                </li>
              ))}
            </ul>
            <button type="button" className="btn btn--outline">
              See Life at Caladium <Icon name="arrow-right" size={16} />
            </button>
          </div>

          <div className="cp-collage">
            <div className="cp-collage__cell cp-collage__cell--lg">
              <img src={`${CIMG}/careers-4.jpeg`} alt="Team meeting" />
            </div>
            <div className="cp-collage__cell cp-collage__cell--tr">
              <img src={`${CIMG}/careers-5.jpeg`} alt="Team celebrating" />
            </div>
            <div className="cp-collage__cell cp-collage__cell--bl">
              <img src={`${CIMG}/careers-6.jpeg`} alt="Caladium team" />
            </div>
            <div className="cp-collage__cell cp-collage__cell--bm">
              <img src={`${CIMG}/careers-7.jpeg`} alt="Office lounge" />
            </div>
            <div className="cp-collage__cell cp-collage__cell--br">
              <img src={`${CIMG}/careers-1.jpeg`} alt="Team activities" />
            </div>
          </div>
        </div>
      </section>

      {/* =================== STATS =================== */}
      <section className="cp-stats-sec">
        <div className="container container--wide">
          <div className="cp-stats">
            {stats.map((s) => (
              <div className="cp-stat" key={s.label}>
                <span className="cp-stat__icon">
                  <Icon name={s.icon} size={22} />
                </span>
                <div className="cp-stat__text">
                  <strong className="cp-stat__value">{s.value}</strong>
                  <span className="cp-stat__label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
